import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { formatDistanceToNowStrict } from 'date-fns';
import { listMcpConnectionsOptions, revokeMcpConnectionMutation } from '@/api';
import type { McpConnection } from '@/api';
import { Button } from '@/components/ui/button';
import { WORKERS } from '@/api/config';

export const Route = createFileRoute('/_auth/mcp')({
  component: AiAppsPage,
});

const FALLBACK_SERVER_URL = `${WORKERS.USERS_WORKER}/mcp`;

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((current) => (current === key ? null : current)), 1600);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return { copied, copy };
}

function AiAppsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(listMcpConnectionsOptions({}));
  const { copied, copy } = useCopy();
  const serverUrl = data?.server_url ?? FALLBACK_SERVER_URL;
  const claudeCode = `claude mcp add --transport http greet ${serverUrl}`;
  const connections = data?.connections ?? [];

  const revoke = useMutation({
    ...revokeMcpConnectionMutation(),
    onSuccess: () => {
      toast.success('Disconnected');
      queryClient.invalidateQueries({ queryKey: listMcpConnectionsOptions({}).queryKey });
    },
    onError: () => toast.error("Couldn't disconnect"),
  });

  return (
    <div className="max-w-xl">
      <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-neutral-900 md:text-[30px]">
        AI apps
      </h1>
      <p className="mt-1 text-sm leading-6 text-neutral-600">
        Ask Claude, ChatGPT, Codex or Cursor about your applicants, create sessions and send invites.
        Everyone in the workspace connects their own app.
      </p>

      <section className="mt-6 space-y-2">
        <CopyRow
          label="Server URL"
          value={serverUrl}
          copied={copied === 'url'}
          onCopy={() => copy('url', serverUrl)}
        />
        <CopyRow
          label="Claude Code"
          value={claudeCode}
          copied={copied === 'claude'}
          onCopy={() => copy('claude', claudeCode)}
        />
        <p className="pt-1 text-sm text-neutral-600">
          In Claude.ai or ChatGPT, add a custom connector with the server URL.{' '}
          <a
            href="https://greetai.co/docs/mcp"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-neutral-900 underline underline-offset-4">
            Setup guides
          </a>
        </p>
      </section>

      <h2 className="mt-10 text-[15px] font-semibold text-neutral-900">Connected</h2>
      {isLoading ? (
        <div className="mt-3 h-14 animate-pulse rounded-2xl bg-neutral-100" />
      ) : connections.length === 0 ? (
        <p className="mt-2 text-sm text-neutral-600">
          Nothing connected yet. Your AI app asks you to sign in here the first time it uses Greet.
        </p>
      ) : (
        <ul className="mt-2">
          {connections.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              disabled={revoke.isPending}
              onRevoke={() => revoke.mutate({ path: { connection_id: connection.id } })}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CopyRow({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 py-2 pl-4 pr-2">
      <div className="min-w-0 flex-1">
        <div className="text-xs text-neutral-500">{label}</div>
        <code className="block truncate font-mono text-[13px] text-neutral-900">{value}</code>
      </div>
      <Button
        type="button"
        variant="ghost"
        className="h-9 w-[72px] shrink-0 text-sm text-neutral-700"
        onClick={onCopy}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

function ConnectionRow({
  connection,
  disabled,
  onRevoke,
}: {
  connection: McpConnection;
  disabled: boolean;
  onRevoke: () => void;
}) {
  const access = connection.scopes.includes('write') ? 'Read & write' : 'Read only';
  const used = connection.last_used_at
    ? `used ${formatDistanceToNowStrict(new Date(connection.last_used_at), { addSuffix: true })}`
    : `connected ${formatDistanceToNowStrict(new Date(connection.created_at), { addSuffix: true })}`;
  return (
    <li className="flex min-h-14 items-center justify-between gap-3 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-neutral-100 text-sm font-semibold text-neutral-700">
          {connection.logo_uri ? (
            <img src={connection.logo_uri} alt="" className="size-full object-cover" />
          ) : (
            connection.client_name.charAt(0).toUpperCase()
          )}
        </div>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-neutral-900">{connection.client_name}</div>
          <div className="truncate text-sm text-neutral-600">
            {access} · {used}
          </div>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        className="h-9 shrink-0 px-3 text-sm text-neutral-600"
        disabled={disabled}
        onClick={onRevoke}>
        Disconnect
      </Button>
    </li>
  );
}
